import CONSTANT from '../constants'
import hasRequiredPermissions from './hasRequiredPermissions'

export type RaciType = 'responsible' | 'accountable' | 'consulted'

export type RaciRoleOption = {
	label: string
	value: string
	description?: string
}

const RACI_TYPE_SHORT_LABELS: Record<RaciType, string> = {
	responsible: 'Requestor',
	accountable: 'Approver',
	consulted: 'Reader'
}

const buildRaciRoleLabel = (domainLabel: string, raciType: RaciType): string =>
	`${domainLabel} · ${RACI_TYPE_SHORT_LABELS[raciType]}`

type DomainConfig = {
	id: string
	label: string
	responsible: string[]
	accountable: string[]
	consulted: string[]
}

const DOMAIN_CONFIGS: DomainConfig[] = [
	{
		id: 'projects',
		label: 'Projects',
		responsible: CONSTANT.PROJECT_REQUESTOR,
		accountable: CONSTANT.PROJECT_APPROVER,
		consulted: CONSTANT.PROJECT_READER
	},
	{
		id: 'accounts',
		label: 'Accounts',
		responsible: CONSTANT.ACCOUNT_REQUESTOR,
		accountable: CONSTANT.ACCOUNT_APPROVER,
		consulted: CONSTANT.ACCOUNT_READER
	},
	{
		id: 'tags',
		label: 'Tags',
		responsible: CONSTANT.TAG_REQUESTOR,
		accountable: CONSTANT.TAG_APPROVER,
		consulted: CONSTANT.TAG_READER
	},
	{
		id: 'platforms',
		label: 'Platforms',
		responsible: CONSTANT.PLATFORM_REQUESTOR,
		accountable: CONSTANT.PLATFORM_APPROVER,
		consulted: CONSTANT.PLATFORM_READER
	},
	{
		id: 'applications',
		label: 'Applications',
		responsible: CONSTANT.APPLICATION_REQUESTOR,
		accountable: CONSTANT.APPLICATION_APPROVER,
		consulted: CONSTANT.APPLICATION_READER
	},
	{
		id: 'gcpProjects',
		label: 'GCP Projects',
		responsible: CONSTANT.GCPPROJECT_REQUESTOR,
		accountable: CONSTANT.GCPPROJECT_APPROVER,
		consulted: CONSTANT.GCPPROJECT_READER
	},
	{
		id: 'gcpCerts',
		label: 'GCP Certs',
		responsible: CONSTANT.GCP_CERT_REQUESTOR,
		accountable: CONSTANT.GCP_CERT_APPROVER,
		consulted: CONSTANT.GCP_CERT_READER
	},
	{
		id: 'rhocpTenants',
		label: 'RHOCP Tenants',
		responsible: CONSTANT.RHOCPTENANT_REQUESTOR,
		accountable: CONSTANT.RHOCPTENANT_APPROVER,
		consulted: CONSTANT.RHOCPTENANT_READER
	},
	{
		id: 'mongodb',
		label: 'MongoDB',
		responsible: CONSTANT.MONGODB_REQUESTOR,
		accountable: CONSTANT.MONGODB_APPROVER,
		consulted: CONSTANT.MONGODB_READER
	},
	{
		id: 'microservices',
		label: 'Microservices',
		responsible: CONSTANT.MICROSERVICE_REQUESTOR,
		accountable: CONSTANT.MICROSERVICE_APPROVER,
		consulted: CONSTANT.MICROSERVICE_READER
	},
	{
		id: 'apis',
		label: 'APIs',
		responsible: CONSTANT.API_REQUESTOR,
		accountable: CONSTANT.API_APPROVER,
		consulted: CONSTANT.API_READER
	},
	{
		id: 'clusters',
		label: 'Clusters',
		responsible: CONSTANT.CLUSTER_REQUESTOR,
		accountable: CONSTANT.CLUSTER_APPROVER,
		consulted: CONSTANT.CLUSTER_READER
	},
	{
		id: 'userAccess',
		label: 'User Access',
		responsible: CONSTANT.USERACCESS_REQUESTOR,
		accountable: CONSTANT.USERACCESS_APPROVER,
		consulted: CONSTANT.USERACCESS_READER
	}
]

const RACI_TYPES: RaciType[] = ['responsible', 'accountable', 'consulted']

const permissionsForRole = (domain: DomainConfig, raciType: RaciType): string[] =>
	domain[raciType]

export type RaciRoleOptionGroup = {
	label: string
	options: RaciRoleOption[]
}

export const RACI_ROLE_OPTIONS: RaciRoleOption[] = DOMAIN_CONFIGS.flatMap(
	(domain) =>
		RACI_TYPES.map((raciType) => {
			const roleKey = `${domain.id}:${raciType}`
			return {
				label: buildRaciRoleLabel(domain.label, raciType),
				value: roleKey,
				description: permissionsForRole(domain, raciType).join(', ')
			}
		})
)

export const RACI_ROLE_OPTION_GROUPS: RaciRoleOptionGroup[] = DOMAIN_CONFIGS.map(
	(domain) => ({
		label: domain.label,
		options: RACI_TYPES.map((raciType) => {
			const roleKey = `${domain.id}:${raciType}`
			return {
				label: RACI_TYPE_SHORT_LABELS[raciType],
				value: roleKey,
				description: permissionsForRole(domain, raciType).join(', ')
			}
		})
	})
)

const RACI_ROLE_OPTION_BY_VALUE = new Map(
	RACI_ROLE_OPTIONS.map((option) => [option.value, option])
)

export const normalizeRaciRoleSelection = (
	selectedRoles: ReadonlyArray<{ label: string; value: string }> | undefined
): Array<{ label: string; value: string }> => {
	if (!selectedRoles?.length) {
		return []
	}

	return selectedRoles.map((role) => {
		const normalizedValue = role.value.endsWith(':informed')
			? role.value.replace(':informed', ':consulted')
			: role.value
		const option = RACI_ROLE_OPTION_BY_VALUE.get(normalizedValue)
		return option ?? { ...role, value: normalizedValue }
	})
}

const RACI_ROLE_PERMISSION_MAP: Record<string, string[]> = Object.fromEntries(
	DOMAIN_CONFIGS.flatMap((domain) =>
		RACI_TYPES.map((raciType) => [
			`${domain.id}:${raciType}`,
			permissionsForRole(domain, raciType)
		])
	)
)

export const expandRaciRolesToPermissions = (
	selectedRoles: Array<{ label: string; value: string }> | undefined
): string[] => {
	if (!selectedRoles?.length) {
		return []
	}

	const permissions = new Set<string>()
	selectedRoles.forEach((role) => {
		const roleKey = role.value.endsWith(':informed')
			? role.value.replace(':informed', ':consulted')
			: role.value
		const mapped = RACI_ROLE_PERMISSION_MAP[roleKey]
		if (mapped) {
			mapped.forEach((permission) => permissions.add(permission))
		}
	})
	return Array.from(permissions)
}

export const detectRaciRolesFromPermissions = (
	permissions: string[]
): Array<{ label: string; value: string }> => {
	const permissionSet = new Set(permissions)
	return RACI_ROLE_OPTIONS.filter((option) => {
		const rolePermissions = RACI_ROLE_PERMISSION_MAP[option.value] || []
		return (
			rolePermissions.length > 0 &&
			rolePermissions.every((permission) => permissionSet.has(permission))
		)
	}).map(({ label, value }) => ({ label, value }))
}

export const getPermissionsNotCoveredByRaciRoles = (
	permissions: string[],
	selectedRoles: Array<{ label: string; value: string }>
): string[] => {
	const covered = new Set(expandRaciRolesToPermissions(selectedRoles))
	return permissions.filter((permission) => !covered.has(permission))
}

export const isSystemManagedPermission = (permission: string): boolean =>
	permission.startsWith('useraccess:') && permission.includes('@')

/** Permissions auto-appended by backend on CREATE — hide from editable custom list. */
export const isDefaultBootstrapPermission = (permission: string): boolean =>
	DEFAULT_BOOTSTRAP_PERMISSIONS.has(permission)

const DEFAULT_BOOTSTRAP_PERMISSIONS = new Set([
	'accounts:*:create',
	'projects:*:create',
	'gcpProjects:*:create',
	'applications:*:create',
	'mongodb:*:create',
	'rhocpTenants:*create',
	'tags:*:create',
	'tags:*:read'
])

export const getUnmappedPermissionsForEdit = (
	permissions: string[],
	selectedRoles: Array<{ label: string; value: string }>
): string[] =>
	getPermissionsNotCoveredByRaciRoles(permissions, selectedRoles).filter(
		(permission) =>
			!isSystemManagedPermission(permission) &&
			!isDefaultBootstrapPermission(permission)
	)

export const toCustomPermissionTokens = (
	permissions: string[]
): Array<{ label: string; dismissLabel: string }> =>
	permissions.map((permission) => ({
		label: permission,
		dismissLabel: `Remove item ${permission}`
	}))

const APPROVER_TEAM_LABELS: Record<string, string> = {
	PlatformOwner: 'Platform Owner',
	FinanceTeam: 'Finance Team',
	CloudEconomicsTeam: 'Cloud Economics Team',
	GCPTribe: 'GCP Tribe',
	CloudTribe: 'Cloud Tribe'
}

export type TagApproverTeamPermissionPreset = {
	label: string
	permission: string
}

const TAG_APPROVER_TEAM_IDS = [
	...CONSTANT.APPROVER_TEAMS,
	'GCPTribe',
	'CloudTribe'
] as const

/** Presets for tag-approval team permissions (multi-step tags + GCP Tribe). */
export const TAG_APPROVER_TEAM_PERMISSION_PRESETS: TagApproverTeamPermissionPreset[] =
	TAG_APPROVER_TEAM_IDS.map((team) => ({
		label: `${APPROVER_TEAM_LABELS[team] ?? team} approver`,
		permission: `tags:${team}:*`
	}))

export const toCustomPermissionToken = (
	permission: string
): { label: string; dismissLabel: string } => ({
	label: permission,
	dismissLabel: `Remove item ${permission}`
})

export const isSuperAdmin = (permissions: string[] | undefined): boolean =>
	Boolean(permissions?.includes('*:*:*'))

const ACCESS_ADMIN_PERMISSIONS = [
	'useraccess:*:*',
	'useraccess:*:create',
	'useraccess:*:update',
	'useraccess:*:delete'
]

const USERACCESS_READ_PERMISSION = 'useraccess:*:read'

const grantableUserAccessPermissions = (permissions: string[]): string[] =>
	permissions.filter(
		(permission) =>
			permission.startsWith('useraccess:') &&
			!isSystemManagedPermission(permission)
	)

/** Users who can manage SSP access (superadmin or useraccess create/update/delete). */
export const isAccessAdmin = (permissions: string[] | undefined): boolean => {
	if (!permissions?.length) {
		return false
	}
	if (isSuperAdmin(permissions)) {
		return true
	}

	const userAccessPermissions = grantableUserAccessPermissions(permissions)

	// `useraccess:*:read` alone (User Access Reader) is a member, not an admin.
	if (
		userAccessPermissions.length > 0 &&
		userAccessPermissions.every(
			(permission) => permission === USERACCESS_READ_PERMISSION
		)
	) {
		return false
	}

	return ACCESS_ADMIN_PERMISSIONS.some((adminPermission) =>
		hasRequiredPermissions([adminPermission], permissions)
	)
}

export const buildPermissionsPayload = (
	selectedRoles: Array<{ label: string; value: string }> | undefined,
	customPermissions: Array<{ label: string }> | undefined
): string[] => {
	const permissions = new Set<string>()
	expandRaciRolesToPermissions(selectedRoles).forEach((permission) =>
		permissions.add(permission)
	)
	customPermissions?.forEach((entry) => {
		const trimmed = entry.label?.trim()
		if (trimmed) {
			permissions.add(trimmed)
		}
	})
	return Array.from(permissions)
}

export const formatRaciRolesForDisplay = (
	permissions: string[]
): string[] => detectRaciRolesFromPermissions(permissions).map((role) => role.label)

const DOMAIN_LABEL_BY_ID = Object.fromEntries(
	DOMAIN_CONFIGS.map((domain) => [domain.id, domain.label])
)

const INVALID_PERMISSIONS = new Set(['*:*:*', '-:-:-'])

const extractDomainHighlights = (permissions: string[]): string[] => {
	const highlights = new Set<string>()

	permissions.forEach((permission) => {
		if (
			isSystemManagedPermission(permission) ||
			isDefaultBootstrapPermission(permission) ||
			INVALID_PERMISSIONS.has(permission)
		) {
			return
		}

		const [resource] = permission.split(':')
		const label = resource ? DOMAIN_LABEL_BY_ID[resource] : undefined
		if (label) {
			highlights.add(label)
		}
	})

	return Array.from(highlights)
}

export type UserAccessListSummary = {
	kind: 'super-admin' | 'raci' | 'custom'
	items: string[]
	extraCount: number
}

export const summarizeUserAccessForList = (
	permissions: string[]
): UserAccessListSummary => {
	if (isSuperAdmin(permissions)) {
		return {
			kind: 'super-admin',
			items: ['Full access'],
			extraCount: 0
		}
	}

	const raciLabels = formatRaciRolesForDisplay(permissions)
	if (raciLabels.length > 0) {
		const maxVisible = 2
		return {
			kind: 'raci',
			items: raciLabels.slice(0, maxVisible),
			extraCount: Math.max(0, raciLabels.length - maxVisible)
		}
	}

	const domainHighlights = extractDomainHighlights(permissions)
	const maxVisible = 3

	return {
		kind: 'custom',
		items:
			domainHighlights.length > 0
				? domainHighlights.slice(0, maxVisible)
				: ['Custom access'],
		extraCount: Math.max(0, domainHighlights.length - maxVisible)
	}
}
